import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { usePymbleApi } from "./usePymbleApi";

// const MY_DETAILS_END_POINT = `${api.myPymble}/my-details`;
const MY_DETAILS_END_POINT = "/my/details/";

/**
 * Hook for separate loading of user details data
 * for components
 */
export function useMyDetails() {
  const [myDetails, setMyDetails] = useState<any>({});
  const [extraDetails, setExtraDetails] = useState<any>([]);
  const [myDetailsLoaded, setMyDetailsLoaded] = useState(false);
  const [isStaff, setIsStaff] = useState(false);
  const [isTeacher, setIsTeacher] = useState(false);
  const [isStudent, setIsStudent] = useState(false);
  const [isParent, setIsParent] = useState(false);

  const queryClient = useQueryClient();

  const { callApiS2: callApi, isAuthenticated } = usePymbleApi();

  // Load the contact list from api
  let queryKey = ["my-details"];

  const { isLoading, isError, data, error } = useQuery({
    queryKey,
    queryFn: () => callApi("GET", MY_DETAILS_END_POINT),
    enabled: isAuthenticated, staleTime: 1000 * 60 * 60 * 2, // 2 hours
  });

  // When the data is loaded, set the state
  useEffect(() => {
    if (data) {
      let details = data;
      if (details) {
        setMyDetails(details);
        setExtraDetails(details.ExtraDetails);
        setIsStaff(details?.Roles?.Staff ? true : false);
        setIsParent(details?.Roles?.Parent ? true : false);
        setIsStudent(details?.Roles?.Student ? true : false);
        setIsTeacher(details?.Roles?.Staff && "Teacher" in details.Roles.Staff);
      }
      setMyDetailsLoaded(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data && !isLoading]);

  const hasRoleAttr = (role: string, attr: string) => {
    // eslint-disable-next-line no-unsafe-optional-chaining
    return myDetails && myDetails?.Role && attr in myDetails?.Role[role]
      ? true
      : false;
  };

  const refreshMyDetails = async () => {
    queryClient.invalidateQueries({ queryKey });
  };

  // The app needs both: Home and AddLessonButton call absence admin routes
  // that pymble-apis gates on absence_admin.
  const hasAdminAccess =
    data?.active?.staff === true &&
    data?.permissions?.covers_admin === true &&
    data?.permissions?.absence_admin === true;

  return {
    isLoading,
    isError,
    error,
    myDetails,
    extraDetails,
    refreshMyDetails,
    myDetailsLoaded,
    isParent,
    isStudent,
    isStaff,
    isTeacher,
    hasRoleAttr,
    hasAdminAccess,
  };
}
